import { HtmlBlockPolicy } from '../consts/html-block-policy'

export type IHtmlBlockVisibility = {
  observeLoad(element: HTMLIFrameElement, onLoad: () => void): () => void
  observeRelease(
    id: string,
    element: HTMLIFrameElement,
    onRelease: () => void,
    onVisible?: () => void
  ): () => void
  claim(id: string, onRelease: () => void): void
  release(id: string): void
  touch(id: string): void
}

type ObserverConstructor = new (
  callback: (entries: IntersectionObserverEntry[]) => void,
  options?: IntersectionObserverInit
) => IntersectionObserver

type ObserverWindow = Window & {
  IntersectionObserver?: ObserverConstructor
}

type IdleWindow = Window & {
  requestIdleCallback?: (callback: () => void) => number
  cancelIdleCallback?: (handle: number) => void
}

type ReleaseSubscription = {
  id: string
  onRelease: () => void
  onVisible?: () => void
  timeout: number | null
  idleHandle: number | null
}

type ActiveFrame = {
  onRelease: () => void
  lastVisible: number
  /** Whether this frame is currently on screen. Only offscreen frames may be evicted. */
  visible: boolean
}

export class HtmlBlockVisibility implements IHtmlBlockVisibility {
  private loadObserver: IntersectionObserver | null = null
  private releaseObserver: IntersectionObserver | null = null
  private readonly loadSubscriptions = new Map<HTMLIFrameElement, () => void>()
  private readonly releaseSubscriptions = new Map<HTMLIFrameElement, ReleaseSubscription>()
  private readonly activeFrames = new Map<string, ActiveFrame>()
  private visibilitySequence = 0

  constructor(
    private readonly win: Window,
    private readonly policy: HtmlBlockPolicy
  ) {}

  observeLoad(element: HTMLIFrameElement, onLoad: () => void): () => void {
    const observerWindow = this.win as ObserverWindow
    if (typeof observerWindow.IntersectionObserver !== 'function') {
      onLoad()
      return () => undefined
    }

    this.ensureLoadObserver().observe(element)
    this.loadSubscriptions.set(element, onLoad)

    return () => {
      this.loadSubscriptions.delete(element)
      this.loadObserver?.unobserve(element)
    }
  }

  observeRelease(
    id: string,
    element: HTMLIFrameElement,
    onRelease: () => void,
    onVisible?: () => void
  ): () => void {
    const observerWindow = this.win as ObserverWindow
    if (typeof observerWindow.IntersectionObserver !== 'function') {
      return () => undefined
    }

    this.ensureReleaseObserver().observe(element)
    const subscription: ReleaseSubscription = {
      id,
      onRelease,
      onVisible,
      timeout: null,
      idleHandle: null,
    }
    this.releaseSubscriptions.set(element, subscription)

    return () => {
      this.cancelRelease(subscription)
      this.releaseSubscriptions.delete(element)
      this.releaseObserver?.unobserve(element)
    }
  }

  claim(id: string, onRelease: () => void): void {
    const existing = this.activeFrames.get(id)
    if (existing) {
      existing.onRelease = onRelease
      existing.lastVisible = ++this.visibilitySequence
      existing.visible = true
      return
    }

    while (this.activeFrames.size >= this.policy.limits.concurrentFrames) {
      // Only ever recycle a frame that's actually offscreen — evicting one the
      // user can still see just breaks it silently with no way back (see
      // tasks/2026-09-04-html-block-maximize-eviction-fix/PLAN.md).
      const oldest = this.findOldestEvictableFrame(id)
      if (!oldest) {
        break
      }

      this.activeFrames.delete(oldest.id)
      oldest.frame.onRelease()
    }

    this.activeFrames.set(id, {
      onRelease,
      lastVisible: ++this.visibilitySequence,
      visible: true,
    })
  }

  release(id: string): void {
    this.activeFrames.delete(id)
  }

  touch(id: string): void {
    const active = this.activeFrames.get(id)
    if (active) {
      active.lastVisible = ++this.visibilitySequence
    }
  }

  private ensureLoadObserver(): IntersectionObserver {
    if (!this.loadObserver) {
      const observerWindow = this.win as ObserverWindow
      const Observer = observerWindow.IntersectionObserver
      if (!Observer) {
        throw new Error('IntersectionObserver is unavailable')
      }

      this.loadObserver = new Observer(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.loadSubscriptions.get(entry.target as HTMLIFrameElement)?.()
            }
          })
        },
        { rootMargin: this.policy.visibility.loadRootMargin }
      )
    }

    return this.loadObserver
  }

  private ensureReleaseObserver(): IntersectionObserver {
    if (!this.releaseObserver) {
      const observerWindow = this.win as ObserverWindow
      const Observer = observerWindow.IntersectionObserver
      if (!Observer) {
        throw new Error('IntersectionObserver is unavailable')
      }

      this.releaseObserver = new Observer(
        (entries) => {
          entries.forEach((entry) => {
            const element = entry.target as HTMLIFrameElement
            const subscription = this.releaseSubscriptions.get(element)
            if (!subscription) {
              return
            }

            if (entry.isIntersecting) {
              this.markVisible(subscription.id, true)
              this.cancelRelease(subscription)
              subscription.onVisible?.()
            } else {
              this.markVisible(subscription.id, false)
              this.scheduleRelease(subscription)
            }
          })
        },
        { rootMargin: this.policy.visibility.releaseRootMargin }
      )
    }

    return this.releaseObserver
  }

  private scheduleRelease(subscription: ReleaseSubscription): void {
    this.cancelRelease(subscription)
    subscription.timeout = this.win.setTimeout(() => {
      const idleWindow = this.win as IdleWindow
      const release = (): void => {
        subscription.idleHandle = null
        subscription.onRelease()
      }

      if (typeof idleWindow.requestIdleCallback === 'function') {
        subscription.idleHandle = idleWindow.requestIdleCallback(release)
      } else {
        release()
      }
    }, this.policy.timing.releaseDelayMs)
  }

  private cancelRelease(subscription: ReleaseSubscription): void {
    if (subscription.timeout !== null) {
      this.win.clearTimeout(subscription.timeout)
      subscription.timeout = null
    }

    if (subscription.idleHandle !== null) {
      const idleWindow = this.win as IdleWindow
      idleWindow.cancelIdleCallback?.(subscription.idleHandle)
      subscription.idleHandle = null
    }
  }

  private markVisible(id: string, visible: boolean): void {
    const active = this.activeFrames.get(id)
    if (active) {
      active.visible = visible
    }
  }

  private findOldestEvictableFrame(excludedId: string): { id: string; frame: ActiveFrame } | null {
    let oldest: { id: string; frame: ActiveFrame } | null = null

    for (const [id, frame] of this.activeFrames) {
      if (id === excludedId || frame.visible) {
        continue
      }
      if (oldest && frame.lastVisible >= oldest.frame.lastVisible) {
        continue
      }
      oldest = { id, frame }
    }

    return oldest
  }
}

export const createHtmlBlockVisibility = (
  win: Window,
  policy: HtmlBlockPolicy
): IHtmlBlockVisibility => new HtmlBlockVisibility(win, policy)
